import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { purchaseRecording } from '../../utils/recordingApi';
import { componentClasses } from '../livestreams/utils/livestreamDesignSystem';

const PurchaseModal = ({ 
  recording, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [discountCode, setDiscountCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDiscountCode('');
      setShowDiscountInput(false);
      setIsProcessing(false);
    }
  }, [isOpen, recording]);

  const handlePurchase = async () => {
    if (!recording) return;

    setIsProcessing(true);
    try {
      const checkoutResponse = await purchaseRecording(
        recording.id, 
        discountCode.trim() || null
      );

      // Handle the new backend response structure
      if (checkoutResponse.errorMessage) {
        toast.error(checkoutResponse.errorMessage);
        return;
      }

      // Check if this is a free grant (admin/owner)
      if (checkoutResponse.errorMessage === 'FREE_GRANTED') {
        toast.success('Access granted! You can now watch this recording.');
        onSuccess && onSuccess();
        onClose();
        return;
      }

      if (checkoutResponse.sessionUrl) {
        // Redirect to Stripe checkout
        window.location.href = checkoutResponse.sessionUrl;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to initiate purchase');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) {
      return 'Free';
    }
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !recording) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Purchase Recording
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isProcessing}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Recording Preview */}
          <div className="mb-6">
            <div className="flex items-start gap-4">
              <div className="w-20 h-12 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
                <span className="text-2xl">🎥</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">
                  {recording.title || recording.livestreamTitle || 'Untitled Recording'}
                </h3>
                <div className="text-sm text-gray-500 space-y-1">
                  {recording.courtName && (
                    <div>🏟️ {recording.courtName}</div>
                  )}
                  {recording.recordingDate && (
                    <div>📅 {new Date(recording.recordingDate).toLocaleDateString()}</div>
                  )}
                  {recording.duration && (
                    <div>⏱️ {formatDuration(recording.duration)}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Price Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-lg font-medium text-gray-900">Total Price:</span>
              <span className="text-2xl font-bold text-blue-600">
                {formatPrice(recording.price)}
              </span>
            </div>
            {recording.price === 0 || recording.price === null ? (
              <p className="text-sm text-gray-600 mt-2">
                This recording is free to download and watch.
              </p>
            ) : (
              <p className="text-sm text-gray-600 mt-2">
                Secure payment processed by Stripe
              </p>
            )}
          </div>

          {/* Discount Code */}
          <div className="mb-6">
            {!showDiscountInput ? (
              <button
                onClick={() => setShowDiscountInput(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                disabled={isProcessing}
              >
                Have a discount code?
              </button>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Discount Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    placeholder="Enter discount code"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isProcessing}
                  />
                  <button
                    onClick={() => {
                      setShowDiscountInput(false);
                      setDiscountCode('');
                    }}
                    className="px-3 py-2 text-gray-600 hover:text-gray-800"
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Purchase Benefits */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">What you get:</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                Unlimited access to watch this recording
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                Download the recording in high quality
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                Access from any device
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                No expiration date
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={handlePurchase}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  🛒 Purchase for {formatPrice(recording.price)}
                </>
              )}
            </button>
          </div>

          {/* Security Notice */}
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <div className="flex items-start gap-2">
              <span className="text-blue-600 text-sm">🔒</span>
              <div className="text-xs text-blue-800">
                <p className="font-medium">Secure Payment</p>
                <p>Your payment information is encrypted and secure. We use Stripe for payment processing.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseModal;
